/**
 * API client for communicating with the backend.
 * Handles token management and automatic refresh.
 */

const API_BASE = import.meta.env.VITE_API_URL || '';

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    name: string;
    role: string;
  };
  tenant: {
    id: string;
    name: string;
  };
}

const TOKEN_KEY = 'peg_e_pag_tokens';
const USER_KEY = 'peg_e_pag_user';
const TENANT_KEY = 'peg_e_pag_tenant';

export function getStoredTokens(): AuthTokens | null {
  const raw = localStorage.getItem(TOKEN_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function storeTokens(tokens: AuthTokens): void {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
}

export function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(TENANT_KEY);
}

export function getStoredUser(): LoginResponse['user'] | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function storeUser(user: LoginResponse['user']): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function getStoredTenant(): LoginResponse['tenant'] | null {
  const raw = localStorage.getItem(TENANT_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function storeTenant(tenant: LoginResponse['tenant']): void {
  localStorage.setItem(TENANT_KEY, JSON.stringify(tenant));
}

/**
 * Makes an authenticated API request.
 * Automatically attaches the access token.
 */
export async function apiFetch(
  path: string,
  options: RequestInit = {},
): Promise<Response> {
  const tokens = getStoredTokens();
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  // Only set Content-Type for requests with a body
  if (options.body) {
    headers['Content-Type'] = 'application/json';
  }

  if (tokens?.accessToken) {
    headers['Authorization'] = `Bearer ${tokens.accessToken}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  // If 401, try to refresh
  if (response.status === 401 && tokens?.refreshToken) {
    const refreshResponse = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: tokens.refreshToken }),
    });

    if (refreshResponse.ok) {
      const newTokens = await refreshResponse.json();
      storeTokens(newTokens);

      // Retry original request with new token
      headers['Authorization'] = `Bearer ${newTokens.accessToken}`;
      return fetch(`${API_BASE}${path}`, { ...options, headers });
    } else {
      // Refresh failed, clear auth
      clearAuth();
      window.location.href = '/login';
    }
  }

  return response;
}

/**
 * Login to the system.
 */
export async function login(
  loginName: string,
  password: string,
  tenantId?: string,
): Promise<LoginResponse> {
  const body: Record<string, string> = { login: loginName, password };
  if (tenantId) body.tenantId = tenantId;

  const response = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Login failed');
  }

  const data: LoginResponse = await response.json();
  storeTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
  storeUser(data.user);
  storeTenant(data.tenant);
  return data;
}

/**
 * Logout from the system.
 */
export async function logout(): Promise<void> {
  const tokens = getStoredTokens();
  if (tokens?.refreshToken) {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: tokens.refreshToken }),
      });
    } catch {
      // Ignore logout errors
    }
  }
  clearAuth();
}
